import { z } from "zod";
import { isValidDomainPattern } from "./domains.js";

// Limits taken from the current Nemo wizard and the Navigamo Bot Factory sheet.
export const LIMITS = {
  nameMax: 20,
  greetingMax: 300,
  starterMax: 300,
  startersMax: 3,
  legalNoticesMax: 5,
  legalMax: 500,
  additionalEmailsMax: 5,
  responseInstructionsMax: 10000,
  personaMax: 10000,
  faqMax: 100,
  /** FAQs live in their own table (not the draft); these are the technical ceilings, plans set the counts a customer may use. */
  faqsPerAgentMax: 25_000,
  faqQuestionMax: 500,
  faqAnswerMax: 5000,
  startersFromFaqsMax: 3,
  onboardingMax: 200,
  /** Characters of onboarding details (short facts about the business) an agent can hold in all. */
  onboardingTextMax: 8000,
  /** Technical ceiling per agent; the plan decides how many an account may really hold (billing). */
  documentsMax: 1500,
  /** Largest uploaded file in MB, read by the settings app, the API, the importer and the docs. Bigger needs uploads straight to storage. */
  fileMaxMB: 25,
  onlineSourcesMax: 300,
  /** Files picked from Google Drive in one go (one source). The per-agent document ceiling above still applies to all sources together. */
  drivePicksMax: 500,
} as const;

export const providerSchema = z.enum(["anthropic", "openai", "google"]);

export const modelRefSchema = z.object({
  provider: providerSchema,
  model: z.string().min(1),
});

/** 0-100 sliders as shown in the wizard (steps of 10). */
const slider = z.number().int().min(0).max(100);

export const legalNoticeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), value: z.string().min(1).max(LIMITS.legalMax) }),
  z.object({ kind: z.literal("url"), value: z.url().max(LIMITS.legalMax) }),
]);

export type LegalNotice = z.infer<typeof legalNoticeSchema>;

/** The notices an agent shows, in order: `legalNotices` when it has them, else its single older `legalNotice` as number 1. */
export function legalNoticesOf(config: { legalNotice?: LegalNotice | null; legalNotices?: LegalNotice[] | null }): LegalNotice[] {
  if (config.legalNotices) return config.legalNotices;
  return config.legalNotice ? [config.legalNotice] : [];
}

export const faqSchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
  /** When true the question is shown as a conversation starter. */
  chatInitiator: z.boolean().default(false),
});

/** Predefined answers that never go to the LLM (sheet: "Topics"). */
export const topicSchema = z.object({
  title: z.string().min(1),
  triggers: z.array(z.string().min(1)).min(1),
  answer: z.string().min(1),
  /** Off keeps the topic but the agent ignores it. Missing means on, so agents saved before this existed are unchanged. */
  enabled: z.boolean().default(true),
});

/** Sizes the settings app keeps new and edited topics within. The schema stays permissive so an agent saved with larger ones keeps working. */
export const TOPIC_LIMITS = { count: 30, triggers: 10, trigger: 80, title: 80, answer: 1500 } as const;

export const knowledgeSourceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("onboarding"), title: z.string().min(1), description: z.string().min(1) }),
  z.object({ type: z.literal("text"), text: z.string().min(1) }),
  z.object({ type: z.literal("file"), fileId: z.string().min(1), name: z.string().min(1) }),
  z.object({ type: z.literal("url"), url: z.url(), syncCron: z.string().default("0 0 * * *") }),
  z.object({
    type: z.literal("crawl"),
    url: z.url(),
    sitemap: z.boolean().default(false),
    followLinks: z.boolean().default(true),
    syncCron: z.string().default("0 0 * * *"),
    /** Stay inside the start address' folder (for a site that lives under a path on a host it shares with others). */
    pathScope: z.boolean().default(false),
    /** Read pages that ask search engines not to index them (staging copies of the owner's own site). */
    includeNoindex: z.boolean().default(false),
    /** Read at most this many pages (1-300). Absent = the platform cap (300). The platform cap is never raised by it. */
    maxPages: z.number().int().min(1).max(300).optional(),
  }),
  /**
   * Google Drive. Files the owner picked with the Google Picker (`files`, the narrow `drive.file` permission), or a folder link
   * (`folderUrl`, older sources and later folder support). `keepSynced: false` imports once and then stops syncing.
   */
  z.object({
    type: z.literal("google_drive"),
    folderUrl: z.url().optional(),
    files: z.array(z.object({ id: z.string().regex(/^[A-Za-z0-9_-]{10,}$/), name: z.string().max(300), mimeType: z.string().max(200).optional() })).min(1).max(LIMITS.drivePicksMax).optional(),
    keepSynced: z.boolean().default(true),
    /** A folder shared with the Nemo reader account and verified by its owner (docs/GOOGLE-DRIVE-FOLDERS-DESIGN.md). Set by the owner; checked on every sync. */
    registrationId: z.string().max(80).optional(),
    /** The folder's name, written by the server when the source is added (anything sent by a client is replaced). */
    folderName: z.string().max(300).optional(),
    syncCron: z.string().default("0 0 * * *"),
  }),
  /** OneDrive or SharePoint folder, shared by link. */
  z.object({ type: z.literal("microsoft365"), folderUrl: z.url(), syncCron: z.string().default("0 0 * * *") }),
  /** Azure Blob Storage container (optionally a prefix inside it). */
  z.object({
    type: z.literal("azure_blob"),
    account: z.string().min(1),
    container: z.string().min(1),
    prefix: z.string().default(""),
    syncCron: z.string().default("0 0 * * *"),
  }),
]);

/**
 * A label is a name for a detail ("Company"), shown to visitors and quoted to the model as data. Line breaks and control
 * characters are dropped and it is cut to 80 characters by refusing longer ones, so a pasted form label cannot carry a
 * paragraph of instructions.
 */
/** One line of plain text: line breaks and control characters become spaces. */
// eslint-disable-next-line no-control-regex
export const cleanText = (s: string): string => s.replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029]+/g, " ").replace(/\s+/g, " ").trim();
const leadLabel = z.string().transform(cleanText).pipe(z.string().min(1).max(80));

/**
 * Configs stored before labels were limited (80 characters, one line) are cleaned and cut when they are read, so a
 * published agent never stops answering because of an old label. New writes are still refused when a label is too long.
 */
export function repairStoredConfig(raw: unknown): unknown {
  const lead = (raw as { leadCapture?: { fields?: unknown; pages?: unknown } } | null)?.leadCapture;
  if (!lead || typeof lead !== "object") return raw;
  const fix = (fields: unknown) =>
    Array.isArray(fields) ? fields.map((f) => (f && typeof f === "object" && typeof (f as { label?: unknown }).label === "string" ? { ...f, label: cleanText((f as { label: string }).label).slice(0, 80) || "Detail" } : f)) : fields;
  return {
    ...(raw as object),
    leadCapture: { ...lead, fields: fix(lead.fields), ...(Array.isArray(lead.pages) ? { pages: lead.pages.map((p) => (p && typeof p === "object" ? { ...p, fields: fix((p as { fields?: unknown }).fields) } : p)) } : {}) },
  };
}

export const leadFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-zA-Z0-9]*$/),
  label: leadLabel,
  type: z.enum(["text", "email", "phone"]).default("text"),
  required: z.boolean().default(false),
  /** How to ask for this detail (wording or guidance for the agent). Optional; the order of `fields` is the order of the questions. */
  ask: z.string().max(300).transform(cleanText).optional(),
});

/** When and how the agent asks for a visitor's details. Everything is optional; defaults are in the prompt builder. */
export const leadFlowSchema = z.object({
  /** When to start asking, e.g. "when the visitor asks for a quote". */
  trigger: z.string().max(500).transform(cleanText).optional(),
  /** What to say once the details are saved. */
  confirmation: z.string().max(500).transform(cleanText).optional(),
  /** What to do when the visitor does not want to share details. */
  onRefusal: z.string().max(500).transform(cleanText).optional(),
});

/**
 * Asks something different on some pages. `match` is a pattern for the page's path (for example `/pricing/*`, or a star before the
 * name to ignore a folder in front of it; a star stands for any text, case and a closing slash do not matter). The first rule that matches replaces `fields` and/or `flow`.
 */
export const leadPageRuleSchema = z
  .object({
    match: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .regex(/^[\w\-./*~%]+$/, "Use the page path, for example /pricing/* or a star before the name")
      .refine((m) => !/^\*+\/+$/.test(m), "Use * alone to match every page"),
    // Whatever the page, the visitor must be reachable afterwards: a rule that asks for details asks for an Email or a Phone.
    fields: z
      .array(leadFieldSchema)
      .min(1)
      .max(12)
      .refine((fs) => fs.some((f) => f.type === "email" || f.type === "phone"), "Ask for an Email or a Phone detail on this page too.")
      .optional(),
    flow: leadFlowSchema.optional(),
  })
  .refine((r) => r.fields !== undefined || r.flow !== undefined, { message: "A page rule needs fields or a flow." });

export const leadCaptureSchema = z.object({
  enabled: z.boolean().default(false),
  fields: z.array(leadFieldSchema).default([]),
  notifyEmails: z.array(z.email()).default([]),
  crm: z
    .discriminatedUnion("type", [
      z.object({ type: z.literal("zapier"), webhookUrl: z.url(), signingSecret: z.string().min(16).max(200).optional() }),
      z.object({ type: z.literal("wix"), siteId: z.string().min(1) }),
      z.object({ type: z.literal("hubspot"), connectionId: z.string().min(1) }),
    ])
    .optional(),
  flow: leadFlowSchema.optional(),
  /** Page-specific questions (see `leadPageRuleSchema`). The first matching rule wins; the fields above are the default. */
  pages: z.array(leadPageRuleSchema).max(20).default([]),
  /**
   * The leads are delivered to the partner that runs this agent (its `lead.created` events), so no email or CRM destination
   * is needed. Set only by the partner API.
   */
  viaPartner: z.boolean().default(false),
  /**
   * Optional copy of what the visitor shared, emailed to the visitor. Off unless the owner switches it on; the visitor
   * must also agree in the conversation (see docs/LEADS.md).
   */
  recap: z.object({ enabled: z.boolean().default(false), consentText: z.string().trim().max(300).optional() }).optional(),
});

export const websiteDeploymentSchema = z.object({
  mode: z.enum(["floating", "inline"]).default("floating"),
  position: z.enum(["left", "right"]).default("right"),
  style: z.number().int().min(1).max(3).default(1),
  theme: z.enum(["purple", "green", "navy", "orange", "teal", "pink"]).default("purple"),
  fontFamily: z.string().optional(),
  fontSize: z.enum(["small", "medium", "large"]).default("small"),
  useWebsiteFonts: z.boolean().default(true),
  brandName: z.string().max(60).optional(),
  language: z.enum(["en", "es"]).default("en"),
  /**
   * Visitors may dictate their messages (browser speech recognition). Off by default: the browser's speech service
   * may send the audio to the browser maker, so the customer opts in knowingly.
   */
  voiceInput: z.boolean().default(false),
  /** With voice input on: send the message when the visitor stops speaking instead of letting them review it first. */
  voiceAutoSend: z.boolean().default(false),
  /**
   * Source tracking: during a visit the chat remembers the page and campaign link the visitor arrived from and sends it with
   * the messages, so the owner can see which channel brought a conversation or lead. On by default; the owner can turn it off.
   */
  attribution: z.object({ enabled: z.boolean().default(true) }).default({ enabled: true }),
  /** Exact hosts or wildcard patterns such as `*.example.com`. */
  allowedDomains: z.array(z.string().min(1)).default([]),
  /**
   * Anyone with the link may chat on the hosted page `<settings app>/chat/<agent id>` (no website needed). Off by default.
   * It allows that one page's origin for this agent only; it never adds a domain to `allowedDomains`.
   */
  publicChat: z.boolean().default(false),
});

export const limitsSchema = z.object({
  maxConversationsPerMonth: z.number().int().positive().optional(),
  maxDocuments: z.number().int().positive().default(LIMITS.documentsMax),
  maxDocumentMb: z.number().positive().optional(),
  maxUrls: z.number().int().positive().default(LIMITS.onlineSourcesMax),
});

export const agentStatusSchema = z.enum(["draft", "published", "unpublished"]);

export const agentConfigSchema = z.object({
  name: z.string().min(1).max(LIMITS.nameMax),
  creatorName: z.string().min(1),
  avatarUrl: z.url().optional(),

  /** Job, skills and personality (wizard: "Agent Job, Skills, and Personality"). */
  persona: z.string().min(1).max(LIMITS.personaMax),
  responseInstructions: z.string().min(1).max(LIMITS.responseInstructionsMax),

  greetings: z.array(z.string().min(1).max(LIMITS.greetingMax)).min(1),
  conversationStarters: z.array(z.string().min(1).max(LIMITS.starterMax)).max(LIMITS.startersMax).default([]),
  /** The older single notice. Still accepted (partner integrations send it); `legalNoticesOf` reads it as notice number 1. */
  legalNotice: legalNoticeSchema.optional(),
  /** Several notices, shown in this order after the greeting. When present it replaces `legalNotice`. */
  legalNotices: z.array(legalNoticeSchema).max(LIMITS.legalNoticesMax).optional(),

  model: z.object({
    generative: modelRefSchema,
    embedding: modelRefSchema,
  }),
  /**
   * How much latitude the agent takes with wording, 0 = stay close to the source
   * text, 100 = free to paraphrase and be inventive. Applied as prompt guidance:
   * the Messages API no longer accepts `temperature` (400 on every model from
   * API version 2026-10-21), so it cannot be a sampling parameter.
   */
  creativity: slider.default(20),
  /** Grounding: 0 = may use general knowledge, 100 = answer only from the knowledge base. */
  strictness: slider.default(50),
  /** Never use conversations to improve any model unless the account opts in. */
  allowModelTraining: z.boolean().default(false),

  additionalEmails: z.array(z.email()).max(LIMITS.additionalEmailsMax).default([]),

  knowledge: z.object({
    faqs: z.array(faqSchema).max(LIMITS.faqMax).default([]),
    topics: z.array(topicSchema).default([]),
    sources: z.array(knowledgeSourceSchema).default([]),
  }).default({ faqs: [], topics: [], sources: [] }),

  leadCapture: leadCaptureSchema.default({ enabled: false, fields: [], notifyEmails: [], pages: [], viaPartner: false }),
  deployment: z.object({ website: websiteDeploymentSchema.default(websiteDeploymentSchema.parse({})) }).default({
    website: websiteDeploymentSchema.parse({}),
  }),
  limits: limitsSchema.default(limitsSchema.parse({})),
});

export type AgentConfig = z.infer<typeof agentConfigSchema>;
export type AgentConfigInput = z.input<typeof agentConfigSchema>;

/** What Aura (or an API client) may submit: everything optional, validated for shape only. */
export const agentDraftSchema = agentConfigSchema.partial().extend({
  // The embedding model is chosen and pinned by the platform; drafts do not need to carry it.
  model: z.object({ generative: modelRefSchema, embedding: modelRefSchema.optional() }).optional(),
});
export type AgentDraft = z.infer<typeof agentDraftSchema>;

/** A stored agent: config plus platform fields. */
export const agentSchema = z.object({
  id: z.string().min(1),
  accountId: z.string().min(1),
  status: agentStatusSchema,
  version: z.number().int().positive(),
  createdBy: z.string().min(1),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  config: agentConfigSchema,
});
export type Agent = z.infer<typeof agentSchema>;

export interface PublishIssue {
  path: string;
  message: string;
}

/**
 * Extra checks that must pass before a draft can be published. The schema alone
 * accepts anything that is well-formed; publishing also needs a usable agent.
 */
export function publishIssues(config: AgentConfig): PublishIssue[] {
  const issues: PublishIssue[] = [];
  const web = config.deployment.website;
  if (web.allowedDomains.length === 0 && !web.publicChat) {
    issues.push({ path: "deployment.website.allowedDomains", message: "At least one allowed domain is required to deploy." });
  }
  for (const d of web.allowedDomains) {
    if (!isValidDomainPattern(d)) {
      issues.push({ path: "deployment.website.allowedDomains", message: `"${d}" is not a valid domain. Wildcards must look like *.example.com; entries such as *.com or * would allow any website.` });
    }
  }
  if (config.leadCapture.enabled) {
    if (config.leadCapture.fields.length === 0) {
      issues.push({ path: "leadCapture.fields", message: "Lead capture is on but no fields are defined." });
    }
    const crm = config.leadCapture.crm;
    if (crm?.type === "zapier" && !crm.webhookUrl.startsWith("https://")) {
      issues.push({ path: "leadCapture.crm.webhookUrl", message: "The webhook address must start with https://" });
    }
    if (config.leadCapture.recap?.enabled && !config.leadCapture.fields.some((f) => f.type === "email")) {
      issues.push({ path: "leadCapture.recap", message: "The email copy needs an Email detail to be collected." });
    }
    if (config.leadCapture.notifyEmails.length === 0 && !config.leadCapture.crm && !config.leadCapture.viaPartner) {
      issues.push({ path: "leadCapture", message: "Lead capture needs a notification email or a CRM destination." });
    }
    (config.leadCapture.pages ?? []).forEach((rule, i) => {
      const keys = (rule.fields ?? []).map((f) => f.key);
      if (new Set(keys).size !== keys.length) issues.push({ path: `leadCapture.pages.${i}.fields`, message: "Each detail needs its own key." });
      if (config.leadCapture.recap?.enabled && rule.fields && !rule.fields.some((f) => f.type === "email")) {
        issues.push({ path: `leadCapture.pages.${i}.fields`, message: "The email copy needs an Email detail to be collected on this page too." });
      }
    });
  }
  const starters = config.conversationStarters.length + config.knowledge.faqs.filter((f) => f.chatInitiator).length;
  if (starters > LIMITS.startersMax) {
    issues.push({ path: "conversationStarters", message: `At most ${LIMITS.startersMax} conversation starters in total (including FAQs marked as chat initiators).` });
  }
  const hasKnowledge =
    config.knowledge.faqs.length > 0 || config.knowledge.topics.length > 0 || config.knowledge.sources.length > 0;
  if (!hasKnowledge && config.strictness >= 80) {
    issues.push({ path: "strictness", message: "High strictness with no knowledge sources would make the agent refuse every question." });
  }
  return issues;
}
